// ENG-5627 (parent ENG-5626) — sender classification.
//
// Decides whether an inbound message is a genuine end user (counts toward the
// product metric) or non-end-user traffic to exclude: synthetic probes, kanban
// / scheduled-task injections, manager nudges, peer agents, bots. Pure +
// browser-safe; the API calls this at ingest and stores the result in
// conversations.sender_class.

import type { SenderClass } from './types.js';

/**
 * Content markers that identify system-injected direct-chat traffic (not a
 * real end user typing in the webapp). Extend this list as additional
 * injection sources are confirmed to land in direct_chat_messages — keeping
 * them here keeps classification in one auditable place.
 *
 * - synthetic-health-check: the agent synthetic probe (ENG-5122) inserts a
 *   direct_chat_messages row whose content carries `(synthetic-health-check <id>)`.
 */
export const SYSTEM_CONTENT_MARKERS: readonly RegExp[] = [
  /\(synthetic-health-check\b/i,
] as const;

export interface SlackSenderInput {
  channel: 'slack';
  userId?: string | null;
  isBot?: boolean;
  botId?: string | null;
}

export interface TelegramSenderInput {
  channel: 'telegram';
  userId?: string | null;
  isBot?: boolean;
  /** True when the sender is another managed agent (cross-team peer traffic). */
  isPeerAgent?: boolean;
}

export interface DirectChatSenderInput {
  channel: 'direct-chat';
  /** Message body — matched against SYSTEM_CONTENT_MARKERS. */
  content?: string | null;
  /** Resolved auth user behind the session, when present. */
  authUserId?: string | null;
}

export type SenderClassifyInput =
  | SlackSenderInput
  | TelegramSenderInput
  | DirectChatSenderInput;

/** True when `content` matches any known system-injection marker. */
export function isSystemInjectedContent(content?: string | null): boolean {
  if (!content) return false;
  return SYSTEM_CONTENT_MARKERS.some((re) => re.test(content));
}

/**
 * A bare acknowledgement reply ("ack" / "ack.") - the short answer an agent
 * gives to a synthetic liveness probe. Matched case-insensitively after trimming
 * surrounding whitespace, with an optional trailing full stop. Anything longer
 * (a real sentence that merely starts with "ack…") is NOT matched.
 */
const BARE_ACK_RE = /^ack\.?$/i;

/**
 * True when `text` is a bare acknowledgement ("ack" / "ack.") and nothing more -
 * the short answer an agent gives to a synthetic liveness probe. Exported so the
 * host conversation evaluator (ENG-7137) and the display-hiding predicate below
 * share ONE definition of "ack-only", rather than re-deriving the regex.
 */
export function isBareAck(text?: string | null): boolean {
  return BARE_ACK_RE.test((text ?? '').trim());
}

/**
 * ENG-6878: should this direct-chat message be HIDDEN from the conversation
 * view (the end-user Direct Chat client AND the operator dashboard panel)?
 *
 * Synthetic liveness probes and the agent's bare "ack" reply to them are
 * operational noise, not conversation - they clutter the customer-facing chat
 * (observed on Dwight). This is DISPLAY-ONLY: the rows stay persisted because
 * the synthetic-probe liveness cron and the `sender_class` metric depend on
 * them. This predicate only governs rendering.
 *
 * - The injected probe message itself (any role) - identified by its content
 *   marker (`(synthetic-health-check <id>)`), reusing {@link isSystemInjectedContent}.
 * - The agent's bare `ack` acknowledgement (assistant role only, so a real end
 *   user typing "ack" is never hidden).
 */
export function isHiddenFromConversationView(msg: {
  role?: string | null;
  content?: string | null;
}): boolean {
  if (isSystemInjectedContent(msg.content)) return true;
  if (msg.role === 'assistant' && isBareAck(msg.content)) return true;
  return false;
}

/**
 * Classify the sender of an inbound message.
 *
 * - **Slack / Telegram** — a bot (Slack `bot_id`/`is_bot`, Telegram `is_bot`)
 *   is `system`; a Telegram peer agent is `team`; otherwise `end_user`.
 * - **direct-chat** — content matching a system-injection marker (synthetic
 *   probe, etc.) is `system`; everything else is a genuine `end_user`.
 *
 * Defaults to `end_user` only when nothing marks the message as non-end-user,
 * so the metric never silently drops a real conversation.
 */
export function classifySender(input: SenderClassifyInput): SenderClass {
  switch (input.channel) {
    case 'slack':
      if (input.isBot || (input.botId && input.botId.trim() !== '')) return 'system';
      return 'end_user';
    case 'telegram':
      if (input.isBot) return 'system';
      if (input.isPeerAgent) return 'team';
      return 'end_user';
    case 'direct-chat':
      if (isSystemInjectedContent(input.content)) return 'system';
      return 'end_user';
    default: {
      const _exhaustive: never = input;
      throw new Error(
        `classifySender: unsupported channel ${(_exhaustive as { channel: string }).channel}`,
      );
    }
  }
}

/** Convenience: only end-user conversations count toward the product metric. */
export function countsTowardMetric(senderClass: SenderClass): boolean {
  return senderClass === 'end_user';
}
